import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Loader2, LogIn, UserPlus, Globe, Mail, Lock, User } from 'lucide-react';
import { AriaLogo } from '../components/AriaLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Footer } from '../components/Footer';

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success(t('auth.login_success'));
      } else {
        await register(email, password, name);
        toast.success(t('auth.register_success'));
      }
      navigate('/');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'GOOGLE_ACCOUNT') {
        toast.error(t('auth.google_account_hint'));
      } else {
        const msg = detail || t('auth.error_generic');
        toast.error(typeof msg === 'string' ? msg : t('auth.error_generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(210,25%,6%)] flex items-center justify-center p-4 grain-overlay">
      <div className="absolute top-4 right-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="gap-1.5 text-muted-foreground hover:text-foreground"
          data-testid="login-language-toggle"
        >
          <Globe size={16} />
          <span className="text-xs font-semibold uppercase">{i18n.language}</span>
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-8">
          <AriaLogo size={64} className="mb-4 drop-shadow-lg" />
          <h1 className="text-xl font-bold text-foreground">{t('app.title')}</h1>
          <p className="text-xs text-muted-foreground mt-1">{t('app.subtitle')}</p>
        </div>

        <Card className="bg-[hsl(210,18%,9%)] border-border">
          <CardHeader className="pb-2">
            <Tabs value={mode} onValueChange={setMode} className="w-full">
              <TabsList className="w-full bg-[hsl(210,14%,12%)]">
                <TabsTrigger value="login" className="flex-1 text-xs data-[state=active]:bg-[hsl(210,14%,18%)]" data-testid="login-tab">
                  <LogIn size={13} className="mr-1.5" />
                  {t('auth.login')}
                </TabsTrigger>
                <TabsTrigger value="register" className="flex-1 text-xs data-[state=active]:bg-[hsl(210,14%,18%)]" data-testid="register-tab">
                  <UserPlus size={13} className="mr-1.5" />
                  {t('auth.register')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <CardTitle className="text-base pt-4">
              {mode === 'login' ? t('auth.login_title') : t('auth.register_title')}
            </CardTitle>
            <CardDescription className="text-xs">
              {mode === 'login' ? t('auth.login_desc') : t('auth.register_desc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs text-muted-foreground">
                    <User size={12} className="inline mr-1" />
                    {t('auth.name')}
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('auth.name_placeholder')}
                    className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                    data-testid="register-name-input"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs text-muted-foreground">
                  <Mail size={12} className="inline mr-1" />
                  {t('auth.email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="dr.nom@hopital.fr"
                  required
                  className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                  data-testid="auth-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs text-muted-foreground">
                  <Lock size={12} className="inline mr-1" />
                  {t('auth.password')}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="h-10 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                  data-testid="auth-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 font-semibold bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)]"
                data-testid="auth-submit-button"
              >
                {submitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : mode === 'login' ? (
                  t('auth.login_button')
                ) : (
                  t('auth.register_button')
                )}
              </Button>
            </form>

            {/* Google sign-in (Emergent managed) */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] uppercase text-muted-foreground">{t('auth.or')}</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              className="w-full h-11 gap-2.5 text-sm font-medium border-[hsl(210,14%,22%)] hover:bg-[hsl(210,14%,14%)]"
              data-testid="google-signin-button"
            >
              <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
              </svg>
              {t('auth.google_button')}
            </Button>
          </CardContent>
        </Card>

        <p className="text-[10px] text-muted-foreground text-center mt-6 max-w-sm mx-auto">
          {t('disclaimer_banner')}
        </p>

        <Footer compact />
      </motion.div>
    </div>
  );
};

export default LoginPage;
